/**
 * CheckoutAbandonedModal
 *
 * Shown when a user returns from the checkout domain without completing
 * their order (no `te_clear_cart` cookie). Listens for the
 * `checkout:abandoned` custom event dispatched by CheckoutReturnListener.
 *
 * Dismissed state is stored in sessionStorage so the modal only shows
 * once per session (avoids nagging on repeated back-nav).
 */

import styled from '@emotion/styled';
import { useState, useEffect, useCallback } from 'react';
import { useCartContext } from '@/lib/context/cart-context';
import { env } from '@/lib/utils/env';

const DISMISSED_KEY = 'te_checkout_abandoned_dismissed';

const Overlay = styled.div<{ $closing: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
  animation: ${({ $closing }) => ($closing ? 'camFadeOut' : 'camFadeIn')} 0.25s ease forwards;

  @keyframes camFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes camFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

const Modal = styled.div<{ $closing: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 16px;
  padding: 32px;
  max-width: 440px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: ${({ $closing }) => ($closing ? 'camSlideOut' : 'camSlideIn')} 0.3s ease forwards;

  @keyframes camSlideIn {
    from { opacity: 0; transform: translateY(20px) scale(0.96); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes camSlideOut {
    from { opacity: 1; transform: translateY(0) scale(1); }
    to { opacity: 0; transform: translateY(20px) scale(0.96); }
  }
`;

const CartIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 24px;
  line-height: 1.5;
`;

const PrimaryButton = styled.a`
  display: block;
  width: 100%;
  padding: 14px;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  margin-bottom: 12px;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
`;

const SecondaryButton = styled.button`
  display: block;
  width: 100%;
  padding: 10px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  border: none;
  cursor: pointer;
  text-decoration: underline;
`;

export function CheckoutAbandonedModal() {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const { cartToken } = useCartContext();

  const dismiss = useCallback(() => {
    setClosing(true);
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 250);
  }, []);

  useEffect(() => {
    const handler = () => {
      if (sessionStorage.getItem(DISMISSED_KEY) === '1') return;
      if (!cartToken) return;
      setVisible(true);
    };

    window.addEventListener('checkout:abandoned', handler);
    return () => window.removeEventListener('checkout:abandoned', handler);
  }, [cartToken]);

  if (!visible || !cartToken) return null;

  const checkoutUrl = `${env.CHECKOUT_URL}/checkout/${cartToken}`;

  return (
    <Overlay $closing={closing} onClick={dismiss}>
      <Modal $closing={closing} onClick={(e) => e.stopPropagation()}>
        <CartIcon>🛒</CartIcon>
        <Title>You left items in your cart</Title>
        <Description>
          Your cart is still saved. Complete your checkout now before your items sell out!
        </Description>
        <PrimaryButton href={checkoutUrl}>
          Continue Checkout
        </PrimaryButton>
        <SecondaryButton onClick={dismiss}>
          Maybe Later
        </SecondaryButton>
      </Modal>
    </Overlay>
  );
}
